/**
 * In-memory cache for viewed emails
 * Improves performance when navigating back to previously viewed emails
 *
 * Features:
 * - Simple Map-based storage
 * - Cache key based on container + blobPath or email ID
 * - Automatic clearing on page refresh (no persistence)
 * - Optional size limit to prevent memory issues
 */

const MAX_CACHE_SIZE = 100; // Maximum number of emails to cache
const cache = new Map();

/**
 * Generate cache key from email identifiers
 * @param {object} options - { container, blobPath } or { id }
 * @returns {string} Cache key
 */
function getCacheKey({ container, blobPath, id }) {
  if (container && blobPath) {
    return `blob:${container}:${blobPath}`;
  }
  if (id) {
    return `id:${id}`;
  }
  return null;
}

/**
 * Get email from cache
 * @param {object} options - Email identifier
 * @returns {object|null} Cached email or null if not found
 */
export function getFromCache(options) {
  const key = getCacheKey(options);
  if (!key) return null;

  const entry = cache.get(key);
  if (entry) {
    // Update access time for LRU-style eviction
    entry.lastAccess = Date.now();
    console.log(`[Cache] Hit: ${key}`);
    return entry.email;
  }

  console.log(`[Cache] Miss: ${key}`);
  return null;
}

/**
 * Add email to cache
 * @param {object} options - Email identifier
 * @param {object} email - Email data to cache
 */
export function addToCache(options, email) {
  const key = getCacheKey(options);
  if (!key || !email) return;

  // Evict oldest entry if cache is full
  if (cache.size >= MAX_CACHE_SIZE) {
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [k, v] of cache.entries()) {
      if (v.lastAccess < oldestTime) {
        oldestTime = v.lastAccess;
        oldestKey = k;
      }
    }

    if (oldestKey) {
      cache.delete(oldestKey);
      console.log(`[Cache] Evicted: ${oldestKey}`);
    }
  }

  cache.set(key, {
    email,
    lastAccess: Date.now(),
    addedAt: Date.now()
  });

  console.log(`[Cache] Added: ${key} (size: ${cache.size})`);
}

/**
 * Check if email is in cache
 * @param {object} options - Email identifier
 * @returns {boolean}
 */
export function isInCache(options) {
  const key = getCacheKey(options);
  return key ? cache.has(key) : false;
}

/**
 * Remove email from cache
 * @param {object} options - Email identifier
 */
export function removeFromCache(options) {
  const key = getCacheKey(options);
  if (key && cache.has(key)) {
    cache.delete(key);
    console.log(`[Cache] Removed: ${key}`);
  }
}

/**
 * Clear all cached emails
 */
export function clearCache() {
  const size = cache.size;
  cache.clear();
  console.log(`[Cache] Cleared ${size} entries`);
}

/**
 * Get cache statistics
 * @returns {object} Cache stats
 */
export function getCacheStats() {
  return {
    size: cache.size,
    maxSize: MAX_CACHE_SIZE,
    entries: Array.from(cache.entries()).map(([key, value]) => ({
      key,
      addedAt: new Date(value.addedAt).toISOString(),
      lastAccess: new Date(value.lastAccess).toISOString()
    }))
  };
}

// Export cache for debugging in console
if (typeof window !== 'undefined') {
  window.__emailCache = {
    getStats: getCacheStats,
    clear: clearCache
  };
}
